import React, { useMemo, useState } from 'react';
import type { WizardState, ProtocolRecord, AssignmentRecord, MedicationRecord } from '../types';
import type { Action } from '../state/configReducer';
import { getAssignmentsForProtocol, getMedicationById, getProtocolById } from '../state/configReducer';
import ManageProtocolsModal from '../components/ManageProtocolsModal';

interface Props {
  state: WizardState;
  dispatch: React.Dispatch<Action>;
}

const StepArrangement: React.FC<Props> = ({ state, dispatch }) => {
  const [search, setSearch] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [draggingMedId, setDraggingMedId] = useState<string | null>(null);

  const groupedProtocols = useMemo(() => {
    const term = search.trim().toLowerCase();
    const filtered = state.data.protocols.filter(p =>
      !term || p.name.toLowerCase().includes(term) || p.section.toLowerCase().includes(term)
    );
    const groups = new Map<string, ProtocolRecord[]>();
    for (const p of filtered) {
      const key = p.section || '(ללא קטגוריה)';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(p);
    }
    return Array.from(groups.entries());
  }, [state.data.protocols, search]);

  const selectedProtocol = state.ui.selectedProtocolId
    ? getProtocolById(state.data, state.ui.selectedProtocolId)
    : undefined;

  const assignments: AssignmentRecord[] = selectedProtocol
    ? getAssignmentsForProtocol(state.data, selectedProtocol.id)
    : [];

  const assignedMedicationIds = new Set(assignments.map(a => a.medicationId));

  const onToggleAssignment = (protocolId: string, medicationId: string) => {
    dispatch({ type: 'TOGGLE_ASSIGNMENT', protocolId, medicationId });
  };

  const onSetComment = (medicationId: string, comment: string) => {
    if (!selectedProtocol) return;
    dispatch({
      type: 'SET_ASSIGNMENT_COMMENT',
      protocolId: selectedProtocol.id,
      medicationId,
      comment
    });
  };

  // HTML5 native drag-and-drop reorder, mirroring NewPreemieConfig.jsx pattern.
  // Drop on a row reorders relative to its index in the assignments array.
  const onDragStart = (medId: string) => () => setDraggingMedId(medId);
  const onDragEnd = () => setDraggingMedId(null);

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const onDropOnRow = (targetMedId: string) => (e: React.DragEvent) => {
    e.preventDefault();
    if (!draggingMedId || draggingMedId === targetMedId || !selectedProtocol) return;

    const ordered = assignments.map(a => a.medicationId);
    const fromIdx = ordered.indexOf(draggingMedId);
    const toIdx = ordered.indexOf(targetMedId);
    if (fromIdx === -1 || toIdx === -1) return;
    const [moved] = ordered.splice(fromIdx, 1);
    ordered.splice(toIdx, 0, moved);

    dispatch({
      type: 'REORDER_ASSIGNMENTS',
      protocolId: selectedProtocol.id,
      orderedMedicationIds: ordered
    });
    setDraggingMedId(null);
  };

  // Counts of assignments per protocol (for the left list)
  const countByProtocol = useMemo(() => {
    const m = new Map<string, number>();
    for (const a of state.data.assignments) {
      m.set(a.protocolId, (m.get(a.protocolId) || 0) + 1);
    }
    return m;
  }, [state.data.assignments]);

  return (
    <div className="resus-admin__master-detail">
      <aside className="resus-admin__list-pane" aria-label="רשימת פרוטוקולים להתאמה">
        <div className="resus-admin__list-header">
          <input
            className="resus-admin__search"
            type="search"
            placeholder="חיפוש פרוטוקול..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {groupedProtocols.map(([section, items]) => (
          <section key={section}>
            <div className="resus-admin__list-section-header">{section}</div>
            <ul className="resus-admin__list">
              {items.map(p => (
                <li key={p.id}>
                  <button
                    type="button"
                    className={`resus-admin__list-row${p.id === state.ui.selectedProtocolId ? ' resus-admin__list-row--selected' : ''}`}
                    onClick={() => dispatch({ type: 'SELECT_PROTOCOL', id: p.id })}
                    style={{ width: '100%', border: 'none' }}
                  >
                    <div className="resus-admin__list-row__main">
                      <span className="resus-admin__list-row__name">
                        {p.name || <em className="resus-admin__subtle">(ללא שם)</em>}
                      </span>
                    </div>
                    <span className="resus-admin__list-row__count">
                      {countByProtocol.get(p.id) || 0}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </aside>

      <div className="resus-admin__detail-pane">
        {!selectedProtocol && <p className="resus-admin__subtle">בחרו פרוטוקול מהרשימה.</p>}

        {selectedProtocol && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div>
                <h2 style={{ margin: 0 }}>{selectedProtocol.name || selectedProtocol.id}</h2>
                <span className="resus-admin__subtle">{selectedProtocol.section}</span>
              </div>
              <button
                type="button"
                className="resus-admin__btn resus-admin__btn--accent"
                onClick={() => setShowAddModal(true)}
              >
                + הוסף תרופות לפרוטוקול
              </button>
            </div>

            <p className="resus-admin__field-hint">
              גררו ושחררו לשינוי סדר. ההערה מתחת לכל תרופה תופיע רק בפרוטוקול הזה (גוברת על ההערה הכללית).
            </p>

            {assignments.length === 0 && (
              <p className="resus-admin__subtle">לא משויכות תרופות לפרוטוקול זה.</p>
            )}

            <ol style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {assignments.map(a => {
                const med = getMedicationById(state.data, a.medicationId);
                if (!med) return null;
                return (
                  <li
                    key={a.medicationId}
                    className={`resus-admin__assignment-row${draggingMedId === a.medicationId ? ' resus-admin__assignment-row--dragging' : ''}`}
                    draggable
                    onDragStart={onDragStart(a.medicationId)}
                    onDragEnd={onDragEnd}
                    onDragOver={onDragOver}
                    onDrop={onDropOnRow(a.medicationId)}
                  >
                    <div className="resus-admin__assignment-row__handle" title="גרור לשינוי סדר" aria-label="ידית גרירה">≡</div>
                    <div className="resus-admin__assignment-row__main">
                      <div className="resus-admin__assignment-row__title">
                        {med.name}
                        <span className="resus-admin__subtle" style={{ marginInlineStart: 8, fontWeight: 400 }}>
                          {med.kind === 'drug' ? 'תרופה' : 'דריפ'}
                          {med.howToGive ? ` · ${med.howToGive}` : ''}
                        </span>
                      </div>
                      <textarea
                        className="resus-admin__textarea"
                        rows={2}
                        placeholder="הערה ספציפית לפרוטוקול זה (לא חובה)"
                        value={a.comment}
                        onChange={(e) => onSetComment(a.medicationId, e.target.value)}
                      />
                    </div>
                    <button
                      type="button"
                      className="resus-admin__delete-btn"
                      onClick={() => onToggleAssignment(selectedProtocol.id, a.medicationId)}
                    >
                      הסר
                    </button>
                  </li>
                );
              })}
            </ol>

            {showAddModal && (
              <AddMedicationsModal
                medications={state.data.medications}
                selectedIds={assignedMedicationIds}
                onToggle={(medId) => onToggleAssignment(selectedProtocol.id, medId)}
                onClose={() => setShowAddModal(false)}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

interface AddMedicationsModalProps {
  medications: MedicationRecord[];
  selectedIds: Set<string>;
  onToggle: (medId: string) => void;
  onClose: () => void;
}

const AddMedicationsModal: React.FC<AddMedicationsModalProps> = ({ medications, selectedIds, onToggle, onClose }) => {
  const [search, setSearch] = useState('');
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return medications;
    return medications.filter(m =>
      m.name.toLowerCase().includes(q) ||
      m.id.toLowerCase().includes(q) ||
      (m.howToGive ?? '').toLowerCase().includes(q) ||
      m.kind.includes(q)
    );
  }, [medications, search]);

  return (
    <div className="resus-admin__modal-overlay" onClick={onClose}>
      <div className="resus-admin__modal" onClick={(e) => e.stopPropagation()}>
        <h3 className="resus-admin__modal__title">בחירת תרופות לפרוטוקול</h3>
        <input
          className="resus-admin__search"
          type="search"
          placeholder="חיפוש לפי שם / מזהה / דרך מתן..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ marginBottom: 12 }}
        />
        <div className="resus-admin__modal__list">
          {filtered.length === 0 && <p className="resus-admin__subtle">אין תרופות תואמות.</p>}
          {filtered.map(m => {
            const checked = selectedIds.has(m.id);
            return (
              <label key={m.id} className="resus-admin__modal__list-row">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => onToggle(m.id)}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>{m.name || <em>(ללא שם)</em>}</div>
                  <div className="resus-admin__subtle" style={{ fontSize: '0.8rem' }}>
                    {m.kind === 'drug' ? 'תרופה' : 'דריפ'}{m.howToGive ? ` · ${m.howToGive}` : ''}
                  </div>
                </div>
              </label>
            );
          })}
        </div>
        <div className="resus-admin__modal__actions">
          <button type="button" className="resus-admin__btn resus-admin__btn--primary" onClick={onClose}>סיום</button>
        </div>
      </div>
    </div>
  );
};

export default StepArrangement;
